import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { css } from '@emotion/react'
import styled from '@emotion/styled'

import useAuth from '../../hooks/useAuth';

import Logo from '../../images/logo.png'
import MobileLogo from '../../images/logo-mobile.png'

// link icons
import { CalendarIcon, PersonIcon, SettingsIcon, DashIcon, Settings } from '../../images';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import SupervisedUserCircleRoundedIcon from '@mui/icons-material/SupervisedUserCircleRounded';
import Diversity1RoundedIcon from '@mui/icons-material/Diversity1Rounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import WorkHistoryRoundedIcon from '@mui/icons-material/WorkHistoryRounded';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import MessageRoundedIcon from '@mui/icons-material/MessageRounded';
import SoapRoundedIcon from '@mui/icons-material/SoapRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';

import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import { theme, muiTheme } from '../../utils/theme';

import { Tooltip, Button, Collapse } from '@mui/material';
import { StyledLink } from '../../utils/Common';

import useIsMobile from '../../hooks/useIsMobile';


// Badges
import UnreadMessages from './Badges/UnreadMessages';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  background-color: ${props => props.theme.colors.white};
  grid-row: 2;
  grid-column: 1;
  transition: all .5s;
  /* min-height: 100vh; */
  border-right: 1px solid ${props => props.theme.colors.border};

  &::-webkit-scrollbar {
    display: none;
  }
  
  @media ${props => props.theme.breaks.down('md')} {
    position: fixed;
    width: 20rem;
    z-index: 2000;
    left: 0;
    height: 100vh;
    overflow-y: auto;
    top: 0;

    ${props => !props.open ? css /* closed */ `
      left: -20rem;
    ` : ''}
  }
`

const Cover = styled.div`
  position: absolute;
  top: -5px;
  left: 0;
  height: .6rem;
  width: 100%;
  background-color: white;
`;


const Menu = styled.ul`
  display: flex;
  flex-direction: column;
  /* width: 18rem; */
  margin: 0 auto;
  list-style: none;
  padding: 0 .6rem;

  ${props => !props.$open && css/* closed */`
    padding: 0;
  `}
`

const IconBox = styled.div`
  /* background-color: pink; */
  padding: .4rem;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 1rem;
  ${props => !props.open && css/* closed */`
    /* box-shadow: 0 0 5px rgba(0, 0, 0, .14); */
    box-shadow: ${muiTheme.shadows[4]};
    background-color: ${props.$active ? props.theme.colors.primary : props.theme.colors.body};
  `}
`;

const MenuItemStyles = styled.li`
  width: 100%;
  border-radius: 5px;
  height: 4.5rem;
  background-color: ${props => props.$active && props.open ? props.theme.colors.primary : "transparent"};
  font-weight: ${props => props.$active ? "700" : "500"};
  color: ${props => props.$active ? props.theme.colors.white : props.theme.colors.lightText};
  margin-bottom: 1.2rem;
  position: relative;
  overflow-x: visible;

  &:hover {
    background-color: ${props => props.theme.colors.primaryLighten};
    color: ${props => props.theme.colors.white};

    * {
      color: ${props => props.theme.colors.white};
    }
  }
`

const DropDownItem = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  gap: .5rem;
  justify-content: flex-start;
  align-items: center;
  padding-left: 1rem;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  /* &:hover {
    background-color: ${props => props.theme.colors.primaryLighten};
    color: ${props => props.theme.colors.white};
  } */
`

const LinkStyles = styled(Link)`
  width: 100%;
  height: 100%;
  display: flex;
  gap: .5rem;
  justify-content: flex-start;
  align-items: center;
  padding-left: 1rem;
  text-decoration: none;
  color: inherit;
`

const SubMenuLinkStyles = styled(Link)`
  display: flex;
  gap: 3rem;
  justify-content: flex-start;
  align-items: center;
  padding-left: 2rem;
  text-decoration: none;
  color: inherit;
  color: ${props => props.$active ? props.theme.colors.primary : "#9B9B9F"};
  font-weight: 500;
`;

const SubMenuIndicator = styled.div`
  width: .6rem;
  height: .6rem;
  border-radius: 50%;
  background-color: ${props => props.$active ? props.theme.colors.primary : "#9B9B9F"};
`;

const MainPanel = styled.div`
  position: relative;
  width: 100%;
  padding-top: 2rem;
  flex-grow: 1;
  box-shadow: ${muiTheme.shadows[2]};
`

const DropDown = styled.div`
  margin-left: auto;
  margin-right: 1rem;
`;

const CollapseMenu = styled.div`
  padding-top: .8rem;
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
  margin-bottom: .5rem;
`;

const links = [
  // {
  //   text: 'Dashboard', 
  //   link: '/dashboard', 
  //   icon: (color) => <DashIcon color={color} />,
  //   permission: {subject: 'Dashboard', action: 'read'}
  // },
  {
    text: 'Calendar', 
    link: '/calendar',
    icon: (color) => <CalendarMonthRoundedIcon htmlColor={color} />,
    permission: {subject: 'Calendar', action: 'read'}
  },
  // {
  //   text: 'FullCalendar', 
  //   link: '/calendar-full',
  //   icon: (color) => <CalendarIcon color={color} />
  // },
  // {
  //   text: 'Schedule', 
  //   link: '/schedule/edit',
  //   icon: (color) => <EditCalendarRoundedIcon htmlColor={color} />,
  //   permission: {subject: 'Schedule', action: 'read'}
  // },
  {
    text: 'Customers', 
    link: '/customers',
    icon: (color) => <PersonIcon color={color} />,
    permission: {subject: 'Customer', action: 'read'},
    subMenu: [
      {
        text: 'Directory', 
        link: '/customers',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Customer', action: 'read'}
      },
      {
        text: "Invoices", 
        link: '/invoices',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Invoice', action: 'read'}
      },
      {
        text: 'Payments', 
        link: '/payments',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Payment', action: 'read'}
      },
      {
        text: 'Wig Orders', 
        link: '/wig_orders',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'WigOrder', action: 'read'}
      },
      {
        text: 'Appointments', 
        link: '/appointments',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Appointment', action: 'read'}
      },
      {
        text: 'Contact Groups', 
        link: '/contact_groups',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Customer', action: 'read'}
      },
      {
        text: 'SMS Promotions', 
        link: '/promotions',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Message', action: 'read'}
      },
    ]
  },
  {
    text: "Messages",
    link: '/messages',
    icon: (color) => <MessageRoundedIcon htmlColor={color} />,
    permission: {subject: 'Message', action: 'read'},
    badge: <UnreadMessages />
  },
  // {
  //   text: 'Stylists', 
  //   link: '/stylists',
  //   icon: (color) => <Diversity1RoundedIcon color={color} />,
  //   permission: {subject: 'Stylist', action: 'read'}
  // },
  {
    text: 'Vendors', 
    link: '/vendors',
    icon: (color) => <PersonIcon color={color} />,
    permission: {subject: 'Vendor', action: 'read'},
    subMenu: [
      {
        text: 'Directory', 
        link: '/vendors',
        icon: (color) => <PersonIcon color={color} />,
        permission: {subject: 'Vendor', action: 'read'},
      },
      {
        text: 'Bills', 
        link: '/bills',
        icon: (color) => <FactCheckRoundedIcon htmlColor={color} />,
        permission: {subject: 'Bill', action: 'read'}
      },
      {
        text: 'Sold Wigs',
        link: '/vendor_balances',
        icon: (color) => <FactCheckRoundedIcon htmlColor={color} />,
        permission: {subject: 'Vendor', action: 'read'}
      },
      {
        text: 'Vendor Payments',
        link: '/vendor_payments',
        icon: (color) => <FactCheckRoundedIcon htmlColor={color} />,
        permission: {subject: 'VendorPayment', action: 'read'}
      },
      {
        text: 'Shipments', 
        link: '/shipments',
        icon: (color) => <Inventory2RoundedIcon htmlColor={color} />,
        permission: {subject: 'Shipment', action: 'read'}
      },
      {
        text: 'Synced Shipments', 
        link: '/synced_shipments',
        icon: (color) => <Inventory2RoundedIcon htmlColor={color} />,
        permission: {subject: 'Shipment', action: 'read'}
      },
      // {
      //   text: 'Requests', 
      //   link: '/requests',
      //   icon: (color) => <FactCheckRoundedIcon htmlColor={color} />,
      //   permission: {subject: 'Order', action: 'read'}
      // },
    ]
  },
  {
    text: 'Inventory', 
    link: '/inventory',
    icon: (color) => <Inventory2RoundedIcon htmlColor={color} />,
    permission: {subject: 'Inventory', action: 'read'}
  },
  {
    text: 'Products', 
    link: '/products',
    icon: (color) => <SoapRoundedIcon htmlColor={color} />,
    permission: {subject: 'Product', action: 'read'}
  },
  {
    text: 'Services', 
    link: '/service_tickets',
    icon: (color) => <ContentCutRoundedIcon color={color} />,
    permission: {subject: 'ServiceTicket', action: 'read'},
    subMenu: [
      {
        text: 'Dashboard', 
        link: '/service_tickets/dashboard',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'ServiceTicket', action: 'read'}
      },
      {
        text: 'List', 
        link: '/service_tickets/',
        icon: (color) => <WorkHistoryRoundedIcon color={color} />,
        permission: {subject: 'ServiceTicket', action: 'read'}
      },
      // {
      //   text: 'Check In', 
      //   link: '/service_tickets/check_in',
      //   icon: (color) => <WorkHistoryRoundedIcon color={color} />,
      //   permission: {subject: 'ServiceTicket', action: 'write'}
      // },
      {
        text: 'Stylists', 
        link: '/stylists',
        icon: (color) => <Diversity1RoundedIcon color={color} />,
        permission: {subject: 'Stylist', action: 'read'}
      },
    ]
  },
  {
    text: "Repairs",
    link: '/repairs',
    icon: (color) => <AutoFixHighRoundedIcon color={color} />,
    permission: {subject: 'Repair', action: 'read'},
    subMenu: [
      {
        text: 'Dashboard', 
        link: '/repair_tickets/dashboard',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'RepairTicket', action: 'read'}
      },
      {
        text: 'List', 
        link: '/repair_tickets/',
        icon: (color) => <WorkHistoryRoundedIcon color={color} />,
        permission: {subject: 'RepairTicket', action: 'read'}
      },
      {
        text: 'Specialists', 
        link: '/specialists',
        icon: (color) => <Diversity1RoundedIcon color={color} />,
        permission: {subject: 'Specialist', action: 'read'}
      },
      // {
      //   text: 'Check In', 
      //   link: '/repair_tickets/check_in',
      //   icon: (color) => <WorkHistoryRoundedIcon color={color} />,
      //   permission: {subject: 'RepairTickets', action: 'write'}
      // },
    ]
  },
  {
    text: 'Reports',
    link: '/reports',
    icon: (color) => <AssessmentRoundedIcon htmlColor={color} />,
    permission: {subject: 'Report', action: 'read'}
  },
  {
    text: 'Settings', 
    link: '/settings',
    icon: (color) => <SettingsIcon color={color} />,
    permission: {subject: 'Setting', action: 'read'},
    subMenu: [
      {
        text: 'Services', 
        link: '/services',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'Service', action: 'read'}
      },
      {
        text: 'Repairs', 
        link: '/repairs',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'Repair', action: 'read'}
      },
      {
        text: 'Working Hours', 
        link: '/settings/working_hours',
        icon: (color) => <WorkHistoryRoundedIcon color={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      {
        text: 'Calendar', 
        link: '/settings/calendar',
        icon: (color) => <CalendarMonthRoundedIcon color={color} />,
        permission: {subject: 'Setting', action: 'manage'}
      },
      {
        text: "SMS",
        link: '/settings/sms',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      {
        text: "Credit Card",
        link: '/settings/cc',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      // {
      //   text: 'Quickbooks', 
      //   link: '/settings/quickbooks',
      //   icon: (color) => <SettingsIcon color={color} />,
      //   permission: {subject: 'SystemSetting', action: 'manage'}
      // },
      {
        text: 'Inventory', 
        link: '/settings/inventory/items',
        icon: (color) => <SettingsIcon color={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      {
        text: 'Import Data', 
        link: '/settings/import',
        icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      {
        text: 'Payroll', 
        link: '/settings/payroll',
        icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      },
      {
        text: 'General', 
        link: '/settings/general',
        icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
        permission: {subject: 'SystemSetting', action: 'manage'}
      }
    ]
  },
  {
    text: 'Admin', 
    link: '/users',
    icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
    permission: {subject: 'User', action: 'manage'},
    subMenu: [
      {
        text: 'Users', 
        link: '/users',
        icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
        permission: {subject: 'User', action: 'manage'}
      },
      {
        text: 'Roles', 
        link: '/roles',
        icon: (color) => <SupervisedUserCircleRoundedIcon htmlColor={color} />,
        permission: {subject: 'Role', action: 'manage'}
      },
    ]
  },
  // {
  //   text: 'Portal', 
  //   link: '/vendor-portal',
  //   icon: (color) => <ExitToAppRoundedIcon htmlColor={color} />,
  //   permission: {subject: 'Portal', action: 'read'}
  // },
]

const DropDownBlock = ({isParent, children, to, toggleOpen}) => {
  return (
    !isParent ?
    <LinkStyles to={to} onClick={toggleOpen}>
      {children}
    </LinkStyles>
    :
    <DropDownItem>
      {children}
    </DropDownItem>
  )
}

const SubMenuLink = ({to, text, icon, active, toggleOpen}) => {
  return (
    <SubMenuLinkStyles to={to} $active={active} onClick={toggleOpen}>
      <SubMenuIndicator $active={active} />
      <span>{text}</span>
    </SubMenuLinkStyles>
  )
}

const MenuItem = ({to, text, icon, active, sideBarOpen, subMenu = [], handleExpand, expanded = {}, path, toggleOpen, badge = null}) => {
  const { ability } = useAuth();
  let color = active || expanded[to] ? 'white' : 'inActive'; // is currently overriden by the IconBox color
  let isParent = subMenu?.length ? true : false;

  const { isMobile } = useIsMobile();

  const toggleSideBar = () => {
    if (isMobile) { // in mobile view the sidebar has to be closed after menu selection
      toggleOpen();
    }
  }

  return (
    <>
      <MenuItemStyles $active={active || expanded[to]} open={sideBarOpen} onClick={() => isParent && handleExpand(to)}>
        <DropDownBlock isParent={isParent} to={to} toggleOpen={toggleSideBar}>
          {badge}
          <Tooltip title={text}>
            <IconBox open={sideBarOpen} $active={active}>{icon(color)}</IconBox>
          </Tooltip>
          <span>{sideBarOpen ? text : ""}</span>
          {subMenu?.length ? <DropDown>{!expanded[to] ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}</DropDown> : null}
        </DropDownBlock>
      </MenuItemStyles>
      {subMenu?.length ?
        <Collapse in={expanded[to]} timeout="auto" unmountOnExit sx={{marginBottom: "1.2rem"}}>
          <CollapseMenu>
            {subMenu.map(({text, link, permission, icon}) => {
              if (permission &&
                !ability.can(permission.action, permission.subject) ) {
                return null
              }
              return (
              <SubMenuLink key={text} to={link} text={text} icon={icon("green")} active={link === path} toggleOpen={toggleSideBar} />)
            }
            )}
          </CollapseMenu>
        </Collapse>
      : null}
    </>
  )
}

const Drawer = ({ open, toggleOpen }) => {

  const location = useLocation()
  const currentPath = location.pathname
  const mainPath = currentPath.split('/')[1]
  const path = `/${mainPath}`

  const [expanded, setExpanded] = useState({});

  const { ability } = useAuth();

  const handleExpand = (id) => {
    setExpanded(expanded => ({...expanded, [id]: !expanded[id]}));
  }

  return (
    <Container open={open}>
      <MainPanel>
        <Cover />
        <Menu $open={open}>
          {links.map(({link, text, permission, icon, subMenu, badge}) => {
            if (permission &&
              !ability.can(permission.action, permission.subject) ) {
              return null
            } 
         return ( <MenuItem 
            sideBarOpen={open} 
            key={text} 
            active={link === path} 
            to={link} 
            text={text} 
            icon={icon} 
            path={path}
            subMenu={subMenu}
            handleExpand={handleExpand}
            expanded={expanded}
            toggleOpen={toggleOpen}
            badge={badge}
          />)
        }
          )}
        </Menu>
      </MainPanel>
    </Container>
  )
}

export default Drawer;